import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import './Results.css';

const API         = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const POLYGONSCAN = 'https://polygonscan.com/address/';

function shorten(addr) {
  if (!addr) return '—';
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function formatUSDC(raw) {
  if (raw == null) return '—';
  return `$${(Number(raw) / 1_000_000).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const GAME_META = {
  fortnite: { label: 'FN',     color: '#FFDE00', bg: '#FFDE0015', border: '#FFDE0033' },
  cs2:      { label: 'CS2',    color: '#FF6B00', bg: '#FF6B0015', border: '#FF6B0033' },
  apex:     { label: 'APEX',   color: '#00D4FF', bg: '#00D4FF15', border: '#00D4FF33' },
  tarkov:   { label: 'TARKOV', color: '#C0A060', bg: '#C0A06015', border: '#C0A06033' },
};

function GameBadge({ game }) {
  const b = GAME_META[game] || GAME_META.apex;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
      background: b.bg, color: b.color, border: `1px solid ${b.border}` }}>
      {b.label}
    </span>
  );
}

function winnerName(t) {
  return t.winner_tarkov_id || t.winner_epic_id || t.winner_steam_id || t.winner_apex_id || shorten(t.winner_wallet) || '—';
}

// ── Player history panel ──────────────────────
function PlayerHistory({ gameType, playerId, onClear }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const url = gameType === 'fortnite'
      ? `${API}/user/fortnite/${encodeURIComponent(playerId)}`
      : gameType === 'cs2'
        ? `${API}/user/cs2/${encodeURIComponent(playerId)}`
        : gameType === 'tarkov'
          ? `${API}/user/tarkov/${encodeURIComponent(playerId)}`
          : `${API}/user/${encodeURIComponent(playerId)}`;

    axios.get(url)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(err => {
        setError(err.response?.data?.error || 'Player not found');
        setLoading(false);
      });
  }, [gameType, playerId]);

  if (loading) return <div className="results__status">Searching...</div>;
  if (error) return (
    <div className="results__error">
      {error}{' '}
      <button className="results__clear-btn" onClick={onClear} style={{ marginLeft: 8 }}>Clear</button>
    </div>
  );

  const { user, tournaments } = data;
  const wins  = tournaments.filter(t => t.won).length;
  const total = tournaments.length;
  const displayName = user.epic_player_id || user.steam_player_id || user.apex_player_id || playerId;

  return (
    <div className="results__player-panel">
      <div className="results__player-header">
        <div className="results__player-name">{displayName}</div>
        <div className="results__player-meta">
          <div className="results__player-stat">
            <div className="results__player-stat-val">{total}</div>
            <div className="results__player-stat-label">Played</div>
          </div>
          <div className="results__player-stat">
            <div className="results__player-stat-val">{wins}</div>
            <div className="results__player-stat-label">Won</div>
          </div>
          <div className="results__player-stat">
            <div className="results__player-stat-val">
              {total ? Math.round((wins / total) * 100) : 0}%
            </div>
            <div className="results__player-stat-label">Win Rate</div>
          </div>
        </div>
      </div>

      {tournaments.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No tournament history yet.</div>
      ) : (
        tournaments.map(t => (
          <div key={t.tournament_id} className="results__history-row">
            <span className="results__history-id">#{t.tournament_id}</span>
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>
              {t.window_end ? formatDate(t.window_end) : '—'}
            </span>
            <span className="results__prize">{formatUSDC(Math.floor(Number(t.prize_pool) * 0.9))}</span>
            <span className={t.won ? 'results__history-won' : 'results__history-lost'}>
              {t.won ? '🏆 Won' : 'No win'}
            </span>
          </div>
        ))
      )}

      <button className="results__clear-btn" style={{ marginTop: 16 }} onClick={onClear}>
        Clear
      </button>
    </div>
  );
}

const GAME_TABS = [
  { key: '',         label: 'All Games' },
  { key: 'apex',     label: '🎮 Apex' },
  { key: 'fortnite', label: '⚡ Fortnite' },
  { key: 'cs2',      label: '🔫 CS2' },
  { key: 'tarkov',   label: '☠️ Tarkov' },
];

const SEARCH_GAMES = [
  { key: 'apex',     label: 'Apex' },
  { key: 'fortnite', label: 'Fortnite' },
  { key: 'cs2',      label: 'CS2' },
  { key: 'tarkov',   label: 'Tarkov' },
];

export default function Results() {
  const [tournaments, setTournaments]       = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [gameFilter, setGameFilter]         = useState('');
  const [searchInput, setSearchInput]       = useState('');
  const [searchGame, setSearchGame]         = useState('apex');
  const [searchedPlayer, setSearchedPlayer] = useState(null); // { gameType, playerId }

  const fetchTournaments = useCallback(async () => {
    try {
      const qs = gameFilter ? `?game=${gameFilter}` : '';
      const { data } = await axios.get(`${API}/tournament/history${qs}`);
      setTournaments(data.tournaments);
      setError(null);
    } catch (err) {
      setError('Could not load tournaments. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [gameFilter]);

  useEffect(() => {
    setLoading(true);
    fetchTournaments();
  }, [fetchTournaments]);

  function handleSearch(e) {
    e.preventDefault();
    if (searchInput.trim()) setSearchedPlayer({ gameType: searchGame, playerId: searchInput.trim() });
  }

  const resolved = tournaments.filter(t => t.status === 'resolved');
  const ongoing  = tournaments.filter(t => t.status === 'active' || t.status === 'filling');

  return (
    <div className="results">
      <Helmet>
        <title>Tournament Results — Winners &amp; History | EZGAME</title>
        <meta name="description" content="View completed EZGAME tournament results. See winners, prize payouts, and match history for Apex, Fortnite and CS2 kill tournaments." />
        <link rel="canonical" href="https://www.ezegames.live/results" />
        <meta property="og:type"        content="website" />
        <meta property="og:site_name"   content="EZGAME" />
        <meta property="og:locale"      content="en_US" />
        <meta property="og:title"       content="Tournament Results — Winners &amp; History | EZGAME" />
        <meta property="og:description" content="View completed Apex, Fortnite and CS2 tournament results, winners, and on-chain prize payouts." />
        <meta property="og:url"         content="https://www.ezegames.live/results" />
        <meta property="og:image"       content="https://www.ezegames.live/og-image.png" />
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:site"        content="@EZ_Egames" />
        <meta name="twitter:title"       content="Tournament Results | EZGAME" />
        <meta name="twitter:description" content="Winners, payouts, and match history for Apex, Fortnite and CS2 kill tournaments." />
        <meta name="twitter:image"       content="https://www.ezegames.live/og-image.png" />
      </Helmet>

      <div className="results__header">
        <h1 className="results__title">Tournament <span>Results</span></h1>

        <form className="results__search-row" onSubmit={handleSearch}>
          <select
            className="results__game-select"
            value={searchGame}
            onChange={e => setSearchGame(e.target.value)}
          >
            {SEARCH_GAMES.map(g => (
              <option key={g.key} value={g.key}>{g.label}</option>
            ))}
          </select>
          <div className="results__search-wrap">
            <span className="results__search-icon">⌕</span>
            <input
              className="results__search-input"
              placeholder={
                searchGame === 'fortnite' ? 'Fortnite username...' :
                searchGame === 'cs2'      ? 'Steam64 ID...' :
                searchGame === 'tarkov'   ? 'Tarkov username...' :
                                           'EA / Origin Username...'
              }
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
          <button className="results__search-btn" type="submit" disabled={!searchInput.trim()}>
            Search
          </button>
          {searchedPlayer && (
            <button type="button" className="results__clear-btn"
              onClick={() => { setSearchedPlayer(null); setSearchInput(''); }}>
              Clear
            </button>
          )}
        </form>
      </div>

      {error && <div className="results__error">{error}</div>}

      {searchedPlayer && (
        <>
          <div className="results__section-label">Player History</div>
          <PlayerHistory
            gameType={searchedPlayer.gameType}
            playerId={searchedPlayer.playerId}
            onClear={() => { setSearchedPlayer(null); setSearchInput(''); }}
          />
        </>
      )}

      {/* Game filter tabs */}
      <div className="results__tabs">
        {GAME_TABS.map(tab => (
          <button
            key={tab.key}
            className={`results__tab${gameFilter === tab.key ? ' results__tab--active' : ''}`}
            onClick={() => setGameFilter(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <div className="results__status">Loading...</div>}

      {/* Active / Filling section */}
      {!loading && ongoing.length > 0 && (
        <>
          <div className="results__section-label">Active Now</div>
          <div className="results__table-wrap" style={{ marginBottom: 32 }}>
            <table className="results__table">
              <thead>
                <tr>
                  <th>Game</th><th>#</th><th>Players</th><th>Prize Pool</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {ongoing.map(t => (
                  <tr key={t.id}>
                    <td><GameBadge game={t.game} /></td>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>#{t.id}</td>
                    <td>{t.player_count} / {t.max_players || 100}</td>
                    <td><span className="results__prize">{formatUSDC(Math.floor(Number(t.prize_pool) * 0.9))}</span></td>
                    <td>
                      <span className={`results__status-badge results__status-badge--${t.status}`}>
                        {t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Completed section */}
      {!loading && resolved.length > 0 && (
        <>
          <div className="results__section-label">Completed ({resolved.length})</div>
          <div className="results__table-wrap">
            <table className="results__table">
              <thead>
                <tr>
                  <th>Game</th><th>#</th><th>Winner</th><th>Stats</th><th>Prize Won</th><th>Date</th><th>Verify</th>
                </tr>
              </thead>
              <tbody>
                {resolved.map(t => (
                  <tr key={t.id} className="results__row--winner">
                    <td><GameBadge game={t.game} /></td>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>#{t.id}</td>
                    <td>
                      <div className="results__winner-cell">
                        <span className="results__trophy">🏆</span>
                        <div>
                          <div className="results__winner-addr">{winnerName(t)}</div>
                          {t.winner_wallet && (
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                              {shorten(t.winner_wallet)}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {t.winner_kills != null ? (
                        <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                          <div style={{ color: '#00D4FF', fontWeight: 700 }}>⚔️ {t.winner_kills}k</div>
                          {t.winner_xp != null && (
                            <div style={{ color: 'var(--text-secondary)' }}>+{t.winner_xp.toLocaleString()} xp</div>
                          )}
                        </div>
                      ) : <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>—</span>}
                    </td>
                    <td><span className="results__prize">{formatUSDC(Math.floor(Number(t.prize_pool) * 0.9))}</span></td>
                    <td>{formatDate(t.window_end)}</td>
                    <td>
                      {t.contract_address ? (
                        <a
                          href={`${POLYGONSCAN}${t.contract_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="results__poly-link"
                        >
                          Polygonscan →
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && resolved.length === 0 && ongoing.length === 0 && !error && (
        <div className="results__status">No tournaments yet. Check back after the first one completes!</div>
      )}
    </div>
  );
}
